import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import {
  CollectedAt, MomentumBadge, SideBadge, Sparkline,
} from '@/features/dossier/components/primitives'
import { getWatchlist, type WatchRow } from './api'

/**
 * The watch list — ranked by CHANGE, not by level.
 *
 * Ranking by threat score would put the same three names at the top every week, and a list
 * that never moves is a list nobody opens. The sparkline beside each row is what lets a
 * reader tell a spike from a trend, which a single number cannot — and that difference
 * decides whether anybody should act today.
 */
export function WatchlistPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<WatchRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await getWatchlist())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading the watch list" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Watch List"
        description="Ranked by how much changed this week, not by how high the score is."
      />
      <Panel>
        <ErpTable head={<>
          <Th>Target</Th>
          <Th>Side</Th>
          <Th className="text-right">Signal</Th>
          <Th>7-day</Th>
          <Th>Momentum</Th>
          <Th className="axis-hide-sm">Why watched</Th>
          <Th>Scored</Th>
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={7}
                      message="Nothing on the watch list. Add a target from its own page." />
          )}
          {rows.map((row) => (
            <tr key={row.target.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/opposition/targets/${row.target.id}`)}>
              <Td><span className="font-medium">{row.target.displayName}</span></Td>
              <Td><SideBadge side={row.target.sideCode} /></Td>
              <Td className="text-right tabular-nums">{row.signalScore}</Td>
              <Td>
                {/* One reading is a level; a series is a trend. */}
                <Sparkline values={row.sparkline.length > 1 ? row.sparkline : [0, 0]} />
              </Td>
              <Td><MomentumBadge momentum={row.target.momentumCode} /></Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">{row.reason}</span>
              </Td>
              <Td><CollectedAt at={row.target.scoresAsOf} /></Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
